import { useEffect, useState, type ReactElement } from "react";
import { cn } from "../../../../shared/components/ui/cn";
import { Input } from "../../../../shared/components/ui/input";

interface EditableCellProps {
  readonly value: string | number | null;
  readonly onSave: (value: string | number | null) => Promise<void>;
  readonly type?: "text" | "number";
  readonly className?: string;
  readonly inputClassName?: string;
  readonly buttonClassName?: string;
}

/**
 * Inline editable cell component
 * Click to edit, blur or Enter to save
 */
export function EditableCell({
  value,
  onSave,
  type = "text",
  className,
  inputClassName,
  buttonClassName,
}: Readonly<EditableCellProps>): ReactElement {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value?.toString() || "");
  const [isSaving, setIsSaving] = useState(false);

  // Sync local value when prop changes
  useEffect(() => {
    setLocalValue(value?.toString() || "");
  }, [value]);

  const handleSave = async () => {
    // Parse the new value
    const parsedValue = type === "number" && localValue ? Number.parseInt(localValue, 10) : localValue || null;

    // Check if value has actually changed
    const hasChanged = parsedValue !== value;

    if (!hasChanged) {
      // No change, just exit edit mode
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(parsedValue);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save:", error);
      // Revert on error
      setLocalValue(value?.toString() || "");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBlur = () => {
    void handleSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      void handleSave();
    } else if (e.key === "Escape") {
      setLocalValue(value?.toString() || "");
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <Input
        type={type}
        value={localValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        autoFocus
        className={cn(
          "h-8 text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          inputClassName,
        )}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      className={cn(
        "cursor-pointer hover:bg-neutral-800/50 px-2 py-1 rounded transition-colors min-h-[32px] flex items-center w-full text-left",
        buttonClassName,
      )}
    >
      <span className={className}>{value || <span className="text-neutral-600">—</span>}</span>
    </button>
  );
}
